<template>
    <div class="combination-container">
        <Title v-if="drawDate && drawDate.drawDate" :title="renderDateText(drawDate.drawDate)" :titleOption="`- ${renderHour(drawDate.drawDate)}`" />
        <div class="combination__market">
            <p class="combination__item" :class="{ 'active': selectedMarketId === m.id }" v-for="(m, index) in bettableMarkets"
                :key="index" @click="handleChooseMarket(m)">
                {{ getTranslatedText(m.name) }}
            </p>
            <p class="combination__note" v-if="!bettableMarkets.length">
                {{ getTranslatedText("No selections are available at the current stake limits.") }}
            </p>
        </div>
        <template v-if="!isShowOutCome">
            <p class="combination__note" v-if="bettableMarkets.length"> {{ getTranslatedText(`select up to [maxPick] numbers`, { maxPick: maxPick}) }}</p>
            <ListNumber :numbers="numbers" :isShowHeader="false" :isDisableAddButton="isDisableAddButton"
                :maxPick="maxPick" />
        </template>
        <template v-else>
            <div class="combination__infor">
                <div class="combination__infor--header">
                    <span @click="handleClearCombination"><i class="bi bi-x"></i></span>
                    <p>{{ getTranslatedText('combination') }}</p>
                </div>
                <div class="number-selected">
                    <Number v-for="(n, i) in numbersSelected" :key="i" :number="n.value" :bgColor="n.color_code"
                        :customClass="'number__in-list'" />
                </div>
            </div>

            <CombinationOutcome
                :combinationOutcome="combinationOutcome"
                @back="closeCombinationOutcome"
                @add="handleAddToPrepareBet"
                :maxPick="numbersSelected.length"
            />
        </template>
    </div>
</template>
<script>
import languageMixins from "@/mixins/language-mixins";
import utilsMixin from '@/mixins/function-utils'
import betslipApiMixin from '@/mixins/betslip-api-mixin';
import Title from "@/components/common/title/index.vue";
import ListNumber from "./components/list-number.vue";
import Number from "@/components/common/number";
import CombinationOutcome from "./components/combination-outcome.vue"
import EventBus from '@/utils/EventBus';
import { resolveStake, isOutcomeBettable } from "@/utils/betslip-transform";
import { getStateStore } from "@/utils/utils";
import { notify } from "@/utils/notify";

export default {
    mixins: [languageMixins, utilsMixin, betslipApiMixin],
    components: { Title, Number, ListNumber, CombinationOutcome },
    props: {
        marketSlug: {
            type: String,
        },
        game: {}
    },
    data() {
        return {
            //game: null,
            // Unfiltered source: bettability is derived, so a late accountSetting
            // re-evaluates instead of being stuck with a stale filter.
            rawMarkets: [],
            selectedMarketId: 0,
            numbers: [],
            numbersSelected: [],
            combinationOutcome: [],
            maxPick: 0,
            isShowOutCome: false,
            isDisableAddButton: true,
        }
    },
    computed: {
        drawDate() {
            return this.$store.getters.drawDate(this.$route.params.gameSlug);
        },
        accountSetting() {
            // Live store reference. NOT getStateStore(): that deep-clones the entire
            // state via JSON round-trip, so the computed would depend on all of it and
            // return a new object on every unrelated change.
            return this.$store.state.accountSetting;
        },
        bettableMarkets() {
            return (this.rawMarkets || []).filter((m) =>
                (m.outcomes || []).some((o) => isOutcomeBettable(o, this.accountSetting))
            );
        },
    },
    watch: {
        accountSetting() {
            this.syncSelectedMarket();
        },
    },
    created() {
        this.handleGetDataCombination(this.marketSlug)
        EventBus.on('add-number', this.handleAdd)
    },
    unmounted() {
        EventBus.off('add-number', this.handleAdd)
    },

    methods: {
        mapOutcomesWithDefault(outcomes) {
            return outcomes
                .map((item) => ({
                    ...item,
                    amount: item.bet_default,
                }))
                // Hide selections the player could never bet (payout_max below bet_min).
                .filter((item) => isOutcomeBettable(item, this.accountSetting));
        },

        // Re-derive the visible market and its outcome rows from the raw source. Runs on
        // load and again whenever accountSetting lands, so a slow settings response still
        // ends up filtered rather than leaving the fail-open list in place forever.
        syncSelectedMarket() {
            const markets = this.bettableMarkets;
            if (!markets.length) {
                this.selectedMarketId = 0;
                this.maxPick = 0;
                this.combinationOutcome = [];
                return;
            }
            const current = markets.find((m) => m.id === this.selectedMarketId) || markets[0];
            this.selectedMarketId = current.id;
            this.maxPick = current.config.maxPick;
            const amounts = new Map(this.combinationOutcome.map((o) => [o.id, o.amount]));
            this.combinationOutcome = this.mapOutcomesWithDefault(current.outcomes).map((o) => ({
                ...o,
                amount: amounts.has(o.id) ? amounts.get(o.id) : o.amount,
            }));
        },

        handleGetDataCombination(marketSlug) {
            if (!this.game) return;
            const groups = this.game.groups || [];
            this.numbers = this.game.balls;
            this.rawMarkets = groups.find(m => m.slug == marketSlug)?.markets || [];
            this.syncSelectedMarket();
        },

        handleChooseMarket(m) {
            this.selectedMarketId = m.id;
            this.combinationOutcome = this.mapOutcomesWithDefault(m.outcomes);
            this.isShowOutCome = false;
        },

        handleAddToPrepareBet(outcome) {
            // Clamp to the same value the amount field displays, so the submitted stake
            // matches what the user sees. Fall back to account-level limits when the
            // outcome carries none, so the clamp can never silently no-op.
            const { wager, valid } = resolveStake(outcome.amount, outcome, getStateStore().accountSetting);
            if (!valid) {
                notify({ text: this.getTranslatedText("This selection cannot be bet at the current stake limits."), type: "error" });
                return;
            }
            const payload = {
                bets: [{
                    wager,
                    event_id: this.drawDate.eventId,
                    market_options_id: outcome.id,
                    combination: true,
                    numbers: [this.numbersSelected.map(x => x.value)]
                }]
            };
            this.submitToBetslip(payload, { isCombination: true, outcome });
            this.closeCombinationOutcome();
        },

        handleAdd(data) {
            this.numbersSelected = data.numbersSelected;
            this.resetCombinationOutcomeAmounts();
            this.isShowOutCome = true;
            data.onSuccess?.();
        },

        closeCombinationOutcome() {
            this.isShowOutCome = false;
            this.numbersSelected = [];
        },

        handleClearCombination() {
            this.closeCombinationOutcome();
            this.checkDisableAddButton();
        },

        resetCombinationOutcomeAmounts() {
            this.combinationOutcome.forEach((item) => {
                item.amount = item.bet_default;
            });
        },

        checkDisableAddButton() {
            this.isDisableAddButton = this.numbersSelected.length == 0;
        }
    }

}
</script>

<style lang="scss" scoped>
@import "@/public/scss/components/coupons/combination.scss";
</style>